import { ref } from 'vue'
import type { Raw } from 'vue'
import { v4 as uuidv4 } from 'uuid'

import { Mixpanel } from '@/analytics/mixpanel'
import type {
  AudioArrival,
  AudioPlaybackMetrics,
  AudioPlayAttribution,
  AudioStopReason,
} from '@/analytics/mixpanel/mixpanel'
import { miniPlayer19040Variant } from '@/services/ab/experiments'
import { Sentry } from '@/services/sentry'
import { auth } from '@/services/auth'
import { backend } from '@/services/backend'
import { assertNotNull } from '@/helpers/typing'
import { saveReadingPosition } from '@/services/readingProgress'
import {
  androidLocalAudioFileSrc,
  nativeAudioFileUri,
  isAudioDownloaded,
  loadAudioFromNativeStorage,
} from '@/services/native.storage'
import { platform } from '@/services/platform'
import { audioOutputDeviceDetection } from '@/services/capacitor.audioOutputDeviceDetectionPlugin'
import type { AudioOutputDevice } from '@/services/capacitor.audioOutputDeviceDetectionPlugin'
import type { DocWithContent } from '@/models/interfaces'

import { ChapterPrevNextControl } from './control.chapter.prev-next'
import {
  ChapterProgressControl,
  saveAudioChapterProgress,
} from './control.chapter.progress'
import { MediaSessionControl } from './control.media-session'
import { PlaybackControl } from './control.playback'
import type { PlaybackState } from './control.playback'
import { RateControl } from './control.rate'
import { VolumeControl } from './control.volume'
import { AudioDoc } from './logic'
import { router } from '@/helpers/router'
import { chapterTimings, findChapterAtPosition } from './chapter-timing'
import {
  isNativeAudioAvailable,
  NativeAudio,
  nativeAudioBridge,
} from './native-audio'
import type { AudioEngine } from './audio-engine'
import {
  requestFinalNativeProgressSync,
  requestNativeProgressSync,
  startNativeProgressSync,
} from './native-progress'

/**
 * Audio listening state/metrics accumulated in-memory between matching `Audio Play` and
 * `Audio Stop` events.
 */
interface ActivePlaybackMetrics extends AudioPlaybackMetrics {
  lastPosition: number
  playbackRateAtLastPosition: number
  isAdvancing: boolean
  // Used to ensure that `Audio Stop` is send only after its matching
  // `Audio Play` (it resolves when `Audio Play` has been sent)
  playTracked?: Promise<void>
}

interface PlayerAudioSource {
  /** The URL passed to the audio engine. */
  audioSource: string
  /** Whether playback uses data from a native storage download. */
  isPlaybackFromDownload: boolean
}

interface PreparedAudioEngine {
  element: AudioEngine
  initialChapterIdx?: number
  startPosition: number
}

/**
 * Start timestamp for audio playback.
 *
 * Returns the saved audio position when it belongs to the selected chapter,
 * or the chapter's start time otherwise. `currentChapterIdx` selects the
 * chapter; when omitted, we use the saved reading chapter.
 */
export function audioStartPosition(
  doc: DocWithContent,
  currentChapterIdx?: number,
): number {
  const savedChapterIdx = doc.content.findIndex(
    (page) => page.url_slug === doc.user.position_chapter,
  )
  const chapterIdx =
    currentChapterIdx ?? (savedChapterIdx >= 0 ? savedChapterIdx : 0)
  const chapter = chapterTimings(doc)[chapterIdx]
  const savedPosition = doc.user.audio_position
  return savedPosition > chapter.start &&
    (chapter.end === null || savedPosition < chapter.end)
    ? savedPosition
    : chapter.start
}

function chapterAtAudioPosition(doc: DocWithContent, position: number): number {
  return findChapterAtPosition(chapterTimings(doc), position)?.idx ?? 0
}

/**
 * The audio player session: the doc that is loaded and its audio controls.
 *
 * We export it as a singleton (`playerSession`) below.
 */
export class PlayerSession {
  private _doc: DocWithContent | null = null
  private _element: AudioEngine | null = null
  private _audio: AudioDoc | null = null
  private _playbackControl: PlaybackControl | null = null
  private _chapterControl: ChapterPrevNextControl | null = null
  private _progressControl: ChapterProgressControl | null = null
  private _rateControl: RateControl | null = null
  private _volumeControl: VolumeControl | null = null
  private _mediaSessionControl: MediaSessionControl | null = null
  private _usesNativeAudio = false

  /** True when the audio data comes from a native storage download. */
  private _isPlaybackFromDownload: boolean = false

  private _playbackState: PlaybackState = 'pause'
  private _playbackStartTime: number = 0

  /** Metrics for the current playback, used by its `Audio Stop` event. */
  private _activePlayback: ActivePlaybackMetrics | null = null

  /**
   * Properties saved when playback is requested, including the UI control used
   * and any applicable arrival, queue, or referrer. They are included in the
   * `Audio Play` event if/when playback begins.
   */
  private _pendingPlayAttribution: AudioPlayAttribution | null = null

  /**
   * Information about how the user navigated to a guide, such as `card` or
   * `notification`.
   *
   * This is set before the route changes. After routing reaches the guide's
   * reading page, the value is moved to `_guideArrival` and cleared here.
   */
  private _pendingGuideArrival: AudioArrival | undefined

  /**
   * Store how the user reached the reading page while the reading page is open.
   *
   * This is so that, for example, if they open a guide from a card and start
   * audio later, the `Audio Play` mixpanel event can still report the original
   * card.
   */
  private _guideArrival: AudioArrival = 'in_guide'

  /**
   * Store the reason for the audio being paused (if any), so that we can
   * thread it through to the next `Audio Stop` Mixpanel event.
   */
  private _nextStopReason: AudioStopReason | null = null

  canShowMiniPlayer: boolean = false

  constructor() {
    this.canShowMiniPlayer = miniPlayer19040Variant() === 'B'
  }

  /**
   * The current session's doc. Returns `null` when there is no session.
   */
  get doc(): DocWithContent | null {
    return this._doc
  }

  get audio(): AudioDoc | null {
    return this._audio
  }

  get playbackControl(): PlaybackControl | null {
    return this._playbackControl
  }

  get chapterControl(): ChapterPrevNextControl | null {
    return this._chapterControl
  }

  get progressControl(): ChapterProgressControl | null {
    return this._progressControl
  }

  get rateControl(): RateControl | null {
    return this._rateControl
  }

  get volumeControl(): VolumeControl | null {
    return this._volumeControl
  }

  /**
   * True when a doc is loaded and its controls are ready to be rendered.
   */
  get isReady(): boolean {
    return this._doc !== null && this._audio !== null
  }

  /**
   * True while the audio is playing (or is about to, see `PlaybackControl`).
   */
  get isPlaying(): boolean {
    return this._playbackState === 'play'
  }

  /**
   * Returns true when the given `doc` is already loaded and ready to play.
   */
  isLoaded(doc: DocWithContent): boolean {
    return this.isReady && this._doc?.id === doc.id
  }

  /**
   * Start a new session for `doc`: create the audio engine, build the
   * controls and load the audio.
   *
   * This does not start the playback, it only prepares the session.
   *
   * Any session that was running is stopped first, so the app only ever has
   * one audio engine.
   *
   * @param doc The doc to play.
   * @param chapterChangeCallback Called when the playback moves to another
   *   chapter. Only the full player passes it, to follow the playback with
   *   the reading page it renders.
   * @param currentChapterIdx The chapter to start at. Defaults to the chapter
   *   of the doc's saved reading position, see `AudioDoc`.
   */
  async start(
    doc: DocWithContent,
    chapterChangeCallback?: (chapterIdx: number) => void,
    currentChapterIdx?: number,
  ): Promise<void> {
    const tookOver = await this.prepareDoc(doc, chapterChangeCallback)
    if (tookOver || !doc.audio) {
      return
    }

    const isNativeAudio = isNativeAudioAvailable()
    this._usesNativeAudio = isNativeAudio
    const source = await this.getAudioSource(doc, isNativeAudio)
    this._isPlaybackFromDownload = source.isPlaybackFromDownload
    const preparedAudio = isNativeAudio
      ? await this.createNativeAudio(source, doc, currentChapterIdx)
      : {
          element: this.createAudioElement(source.audioSource),
          initialChapterIdx: currentChapterIdx,
          startPosition: audioStartPosition(doc, currentChapterIdx),
        }
    this._element = preparedAudio.element
    this._audio = new AudioDoc(
      this._element,
      doc,
      preparedAudio.initialChapterIdx,
      preparedAudio.startPosition,
    )

    const audio = assertNotNull(this._audio)
    if (!isNativeAudio) {
      audio.load()
    }

    this._playbackControl = new PlaybackControl(audio, (state: PlaybackState) =>
      this.onPlaybackState(state),
    )
    const playbackControl = assertNotNull(this._playbackControl)
    playbackControl.initEventListeners()
    this.initAudioMetricListeners(audio)

    this._chapterControl = new ChapterPrevNextControl(
      audio,
      (chapterIdx: number) => chapterChangeCallback?.(chapterIdx),
    )

    this._progressControl = new ChapterProgressControl(
      audio,
      isNativeAudio
        ? (): void => {}
        : (progressInPercent: number): void =>
            this.onReadingProgress(progressInPercent),
      isNativeAudio ? async (): Promise<void> => {} : saveAudioChapterProgress,
    )
    assertNotNull(this._progressControl).initEventListeners()

    this._rateControl = new RateControl(audio)

    this._volumeControl = new VolumeControl(audio)
    assertNotNull(this._volumeControl).initEventListeners()

    if (isNativeAudio) {
      audio.addListener('canplay', startNativeProgressSync)
      audio.load()
      return
    }
    this._mediaSessionControl = await MediaSessionControl.initialize(
      audio,
      playbackControl,
      assertNotNull(this._chapterControl),
      {
        play: () =>
          this.play({
            entryPoint: 'media_session',
            referrerPageName: router()?.currentRoute?.value?.fullPath,
          }),
        pause: () => this.pause(),
      },
    )
  }

  /**
   * Prepare a new session for `doc`, or take over an existing session if the
   * doc is already loaded.
   *
   * This does not build the audio controls: it only stops any existing
   * session and keeps `doc` as the session's doc. The Mini Player then renders
   * as a link back to the doc, and `start` builds the controls later on.
   *
   * @returns True when the session of `doc` was already running and was taken over.
   */
  async prepareDoc(
    doc: DocWithContent,
    chapterChangeCallback?: (chapterIdx: number) => void,
  ): Promise<boolean> {
    if (this.isLoaded(doc)) {
      this.takeOver(doc, chapterChangeCallback)
      return true
    }

    await this.stop()

    this._doc = doc
    return false
  }

  /**
   * Hand the running session over to another player.
   */
  private takeOver(
    doc: DocWithContent,
    chapterChangeCallback?: (chapterIdx: number) => void,
  ): void {
    this._doc = doc
    assertNotNull(this._audio).doc = doc
    assertNotNull(this._chapterControl).setChapterChangeCallback(
      (chapterIdx: number) => chapterChangeCallback?.(chapterIdx),
    )
  }

  setChapterChangeCallback(callback: (chapterIdx: number) => void): void {
    this._chapterControl?.setChapterChangeCallback(callback)
  }

  /**
   * Request playback start.
   *
   * If/when the audio does start, include the supplied entry point, arrival,
   * queue source, and referrer in its `Audio Play` event.
   */
  play(attribution: AudioPlayAttribution): void {
    this._nextStopReason = null
    this._pendingPlayAttribution = {
      ...attribution,
      arrival:
        attribution.entryPoint === 'player'
          ? attribution.arrival || this._guideArrival
          : undefined,
    }
    this._playbackControl?.play()
  }

  togglePlayback(attribution: AudioPlayAttribution): void {
    if (this._playbackControl?.paused) {
      this.play(attribution)
    } else {
      this.pause()
    }
  }

  setNextArrival(arrival: AudioArrival): void {
    this._pendingGuideArrival = arrival
  }

  clearNextArrival(): void {
    this._pendingGuideArrival = undefined
  }

  /**
   * Store how the user reached a guide after routing finishes. `fallback` is
   * used for a newly opened guide when no source was recorded. Omitting it
   * preserves the existing value when the route still shows the same guide.
   */
  applyPendingGuideArrival(fallback?: AudioArrival): void {
    const arrival = this._pendingGuideArrival || fallback
    if (arrival) {
      this._guideArrival = arrival
    }
    this._pendingGuideArrival = undefined
  }

  pause(reason: AudioStopReason = 'pause'): void {
    this._nextStopReason = reason
    this._playbackControl?.pause()
  }

  /**
   * Stop the player and resets all its data.
   */
  async stop(reason: AudioStopReason = 'navigate'): Promise<void> {
    this._nextStopReason = reason
    const closingDoc = this._doc
    this._playbackControl?.pause()
    this.finishActivePlayback(reason)
    await this._playbackControl?.stopPositionTracking()
    this._audio?.removeListeners()
    await this._mediaSessionControl?.releaseMediaSession()
    await this._element?.remove()
    if (this._usesNativeAudio && closingDoc) {
      void requestFinalNativeProgressSync(closingDoc)
    }

    this._doc = null
    this._element = null
    this._audio = null
    this._playbackControl = null
    this._chapterControl = null
    this._progressControl = null
    this._rateControl = null
    this._volumeControl = null
    this._mediaSessionControl = null
    this._usesNativeAudio = false
    this._isPlaybackFromDownload = false
    this._playbackState = 'pause'
    this._playbackStartTime = 0
    this._pendingPlayAttribution = null
    this._nextStopReason = null
  }

  /**
   * Handle a playback state change: track the listening time and keep the
   * native media controls in sync.
   */
  private async onPlaybackState(state: PlaybackState): Promise<void> {
    if (state === 'ended') {
      this.finishActivePlayback('ended')
    }

    if (this._usesNativeAudio) {
      this._playbackState = state
      if (state !== 'play') {
        void requestNativeProgressSync()
      }
      return
    }
    if (state === 'play') {
      this._playbackStartTime = Date.now()
    }

    // We transfer from playing to pause / end.
    if (this._playbackState === 'play' && state !== 'play' && this._doc) {
      // Send the mixpanel 'Audio Listen' event.
      const timeDiffInSeconds = Math.floor(
        (Date.now() - this._playbackStartTime) / 1000,
      )
      Mixpanel.trackAudioListen(
        this._doc,
        timeDiffInSeconds,
        this._isPlaybackFromDownload,
      )
    }

    this._playbackState = state

    await this._mediaSessionControl?.setPlaybackState(
      state === 'play' ? 'playing' : 'paused',
    )
  }

  private initAudioMetricListeners(audio: AudioDoc): void {
    audio.addListener('playing', () => this.onAudioPlaying())
    audio.addListener('timeupdate', () => this.flushPlaybackInterval())
    audio.addListener('waiting', () => this.suspendPlaybackInterval())
    audio.addBeforeSeekListener(() => this.suspendPlaybackInterval())
    audio.addListener('seeking', () => this.onAudioSeeking())
    audio.addListener('seeked', () => this.onAudioSeeked())
    audio.addListener('ratechange', () => this.onAudioRateChange())
    audio.addListener('pause', () => this.onAudioPause())
  }

  private onAudioPlaying(): void {
    const audio = this._audio
    const doc = this._doc
    if (!audio || !doc) {
      return
    }

    if (!this._activePlayback) {
      const attribution = this._pendingPlayAttribution || {
        entryPoint: 'player' as const,
        arrival: this._guideArrival,
      }
      this._pendingPlayAttribution = null
      this._activePlayback = {
        playbackId: uuidv4(),
        startedAt: this._playbackStartTime || Date.now(),
        playedSeconds: 0,
        listenedSeconds: 0,
        lastPosition: audio.currentTime,
        playbackRateAtLastPosition: audio.playbackRate,
        isAdvancing: true,
      }

      const playback = this._activePlayback
      const chapterIndex = audio.chapters.current().idx
      const position = audio.currentTime
      const playbackRate = audio.playbackRate
      playback.playTracked = this.trackAudioPlay(
        playback,
        doc,
        chapterIndex,
        position,
        playbackRate,
        attribution,
      )
      return
    }

    this._activePlayback.lastPosition = audio.currentTime
    this._activePlayback.playbackRateAtLastPosition = audio.playbackRate
    this._activePlayback.isAdvancing = true
    this._pendingPlayAttribution = null
  }

  private flushPlaybackInterval(): void {
    const playback = this._activePlayback
    const audio = this._audio
    if (!playback || !audio || !playback.isAdvancing) {
      return
    }

    const delta = audio.currentTime - playback.lastPosition
    if (Number.isFinite(delta) && delta > 0) {
      playback.playedSeconds += delta
      playback.listenedSeconds += delta / playback.playbackRateAtLastPosition
    }
    playback.lastPosition = audio.currentTime
  }

  private suspendPlaybackInterval(): void {
    this.flushPlaybackInterval()
    if (this._activePlayback) {
      this._activePlayback.isAdvancing = false
    }
  }

  private onAudioSeeking(): void {
    if (this._activePlayback) {
      // The DOM event fires after currentTime has changed. The last timeupdate
      // already captured the valid interval, so flushing here would count a
      // forward seek as playback. App-controlled seeks flush before assignment
      // through AudioDoc.addBeforeSeekListener above.
      this._activePlayback.isAdvancing = false
    }
  }

  private onAudioSeeked(): void {
    const playback = this._activePlayback
    const audio = this._audio
    if (!playback || !audio) {
      return
    }
    playback.lastPosition = audio.currentTime
    playback.playbackRateAtLastPosition = audio.playbackRate
    playback.isAdvancing = !audio.paused
  }

  private onAudioRateChange(): void {
    this.flushPlaybackInterval()
    const playback = this._activePlayback
    const audio = this._audio
    if (playback && audio) {
      playback.lastPosition = audio.currentTime
      playback.playbackRateAtLastPosition = audio.playbackRate
    }
  }

  private onAudioPause(): void {
    const reason = this._audio?.ended
      ? 'ended'
      : (this._nextStopReason ?? 'interrupt')
    this._nextStopReason = null
    this.finishActivePlayback(reason)
  }

  /**
   * Finish the active playback and emit its `Audio Stop` event. The play ID
   * pairs it with the `Audio Play` event that started the playback.
   */
  private finishActivePlayback(reason: AudioStopReason): void {
    this.flushPlaybackInterval()
    const playback = this._activePlayback
    const audio = this._audio
    const doc = this._doc
    if (!playback || !audio || !doc) {
      return
    }

    const chapterIndex = audio.chapters.current().idx
    const position = audio.currentTime
    const playbackRate = audio.playbackRate
    void this.trackAudioStop(
      playback,
      doc,
      chapterIndex,
      position,
      playbackRate,
      reason,
      Date.now(),
    )
    this._activePlayback = null
  }

  private async trackAudioPlay(
    playback: ActivePlaybackMetrics,
    doc: DocWithContent,
    chapterIndex: number,
    position: number,
    playbackRate: number,
    attribution: AudioPlayAttribution,
  ): Promise<void> {
    let outputDevice: AudioOutputDevice | null = null
    try {
      outputDevice = await audioOutputDeviceDetection.getDevice()
    } catch (error) {
      // If audio output device information isn't found we can just send
      // the playback update without it.
      Sentry.captureException(error)
    }
    Mixpanel.trackAudioPlay(
      playback,
      doc,
      chapterIndex,
      position,
      playbackRate,
      attribution,
      outputDevice ?? undefined,
    )
  }

  private async trackAudioStop(
    playback: ActivePlaybackMetrics,
    doc: DocWithContent,
    chapterIndex: number,
    position: number,
    playbackRate: number,
    reason: AudioStopReason,
    stoppedAt: number,
  ): Promise<void> {
    await playback.playTracked
    Mixpanel.trackAudioStop(
      playback,
      doc,
      chapterIndex,
      position,
      playbackRate,
      reason,
      stoppedAt,
    )
  }

  /**
   * Save the reading position of the chapter that is playing.
   *
   * The playing chapter is not necessarily the page the reader is on: the
   * Mini Player plays outside the reading pages.
   */
  private onReadingProgress(progressInPercent: number): void {
    const doc = this._doc
    const audio = this._audio
    if (!doc || !audio) {
      return
    }

    const content = doc.content[audio.chapters.current().idx]
    saveReadingPosition(doc, content, progressInPercent).catch(
      (error: unknown) => Sentry.captureException(error),
    )
  }

  /**
   * Create the <audio> element that will be played.
   *
   * We need to have an <audio> element in the page to actually play the audio,
   * but it has no `controls`, so it renders nothing. We render the UI in `Player.vue`
   * or `MiniPlayer.vue`.
   */
  private createAudioElement(src: string): HTMLAudioElement {
    const element = document.createElement('audio')
    // Has to be set before the `src`, the browser starts loading right away.
    element.crossOrigin = 'anonymous'
    element.setAttribute('type', 'audio/mpeg')
    element.src = src
    document.body.appendChild(element)
    return element
  }

  private async createNativeAudio(
    source: PlayerAudioSource,
    doc: DocWithContent,
    currentChapterIdx?: number,
  ): Promise<PreparedAudioEngine> {
    const userId = assertNotNull(auth.getUser()).id
    let nativePosition: number | null = null
    try {
      const result = await nativeAudioBridge.resumePosition({
        userId,
        docId: doc.id,
        allowAcknowledged: !navigator.onLine,
      })
      if (
        result.position !== null &&
        (currentChapterIdx === undefined ||
          chapterAtAudioPosition(doc, result.position) === currentChapterIdx)
      ) {
        nativePosition = result.position
      }
    } catch (error) {
      Sentry.captureException(error)
    }
    const savedPosition = audioStartPosition(doc, currentChapterIdx)
    const startPosition =
      nativePosition === null ? savedPosition : Math.max(0, nativePosition)
    return {
      element: new NativeAudio(
        source.audioSource,
        doc,
        startPosition,
        source.isPlaybackFromDownload,
        userId,
      ),
      initialChapterIdx:
        nativePosition === null
          ? currentChapterIdx
          : chapterAtAudioPosition(doc, startPosition),
      startPosition,
    }
  }

  /**
   * Get the source for the selected audio engine. Use a device download when
   * available, or the remote URL otherwise. The web engine may copy an iOS
   * download into the browser cache before returning.
   */
  private async getAudioSource(
    doc: DocWithContent,
    isNativeAudio: boolean,
  ): Promise<PlayerAudioSource> {
    if (isNativeAudio) {
      const downloaded = await isAudioDownloaded(doc)
      let audioSource = doc.audio?.url || ''
      if (downloaded) {
        audioSource = await nativeAudioFileUri(doc)
      }
      return {
        audioSource,
        isPlaybackFromDownload: downloaded,
      }
    }
    if (await platform.isNativeIOSApp()) {
      // In the iOS app, transfer the audio data from native storage into the browser
      // cache, if the data is downloaded to native storage and not yet in browser
      // cache.
      // `isPlaybackFromDownload` will be true if the audio data got transferred
      // from native storage to browser cache now or is still present from a previous
      // transfer.
      // `isPlaybackFromDownload` will be false if the audio gets streamed from
      // the internet, or from browser cache but the cache was not populated through
      // native storage.
      return {
        isPlaybackFromDownload: await loadAudioFromNativeStorage(doc),
        audioSource: doc.audio?.url || '',
      }
    }
    if (
      (await platform.isNativeAndroidApp()) &&
      (await isAudioDownloaded(doc))
    ) {
      // If in the Android app and the audio data is downloaded to native storage,
      // use the native storage file directly as the source for the audio element.
      // This is only possible on Android. On iOS, it does not work because of a CORS
      // issue, see https://github.com/shortformhq/main/issues/6169#issuecomment-1225582895
      return {
        isPlaybackFromDownload: true,
        audioSource: await androidLocalAudioFileSrc(doc),
      }
    }
    // Otherwise, i.e. on Android without the audio data downloaded or on web (where
    // downloads are not supported), simply use the regular web URL of the audio file.
    return {
      isPlaybackFromDownload: false,
      audioSource: doc.audio?.url || '',
    }
  }
}

// The Raw type here prevents Vue from stripping the private properties from the type,
// which causes a bunch of type errors when we expect a `PlayerSession` instance in any component.
export const playerSession = ref<Raw<PlayerSession>>(new PlayerSession())

/**
 * Start a session with the doc the user viewed most recently.
 *
 * The Mini Player calls this when the app starts.
 */
export async function resumePlayerSession(): Promise<void> {
  if (!auth.loggedIn()) {
    return
  }

  // Make sure that the Mini Player will be displayed.
  playerSession.value.canShowMiniPlayer = true

  try {
    const doc = await backend.getLatestDoc()
    if (!doc) {
      return
    }
    await playerSession.value.start(doc)
  } catch (error) {
    Sentry.captureException(error)
  }
}
